import type React from 'react';
import { useLocation } from 'react-router';
import { Icons } from '@shared/constants';
import { useDemoMode } from '../../demo/DemoContext';
import { useRemoteConfig } from '../../context/RemoteConfigContext';

export interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}

/**
 * The bottom-nav destinations, in order. Shared so the pill nav and the top bar
 * agree on what "a nav page" is: the top bar shows a back arrow on every page
 * that is NOT one of these, and the set shifts with the flag below (Flow
 * appears only for flagged testers), so deriving both from one list is what
 * keeps the back arrow from going missing or doubling up.
 */
export const useNavItems = (): NavItem[] => {
  const { routePrefix } = useDemoMode();
  const { whereItGoesEnabled } = useRemoteConfig();

  return [
    { to: `${routePrefix}/`, icon: Icons.Home, label: 'Home' },
    { to: `${routePrefix}/reflect`, icon: Icons.Activity, label: 'Reflect' },
    // Pause holds the third slot; Grow lives in the hamburger menu.
    { to: `${routePrefix}/pause`, icon: Icons.Pause, label: 'Pause' },
    // Hidden feature under test — nav slot appears only for flagged testers.
    ...(whereItGoesEnabled ? [{ to: `${routePrefix}/flow`, icon: Icons.Waves, label: 'Flow' }] : []),
    { to: `${routePrefix}/story`, icon: Icons.Compass, label: 'Story' },
  ];
};

const normalize = (path: string) => (path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path);

/** True when the page on screen is one of the bottom-nav destinations. */
export const useIsBottomNavRoute = (): boolean => {
  const items = useNavItems();
  const { pathname } = useLocation();
  const here = normalize(pathname);
  return items.some(item => normalize(item.to) === here);
};
